import React, { useState, useContext, useEffect } from "react";
import LoadingButton from "./ActividadesUtils/LoadingButton";
import { Trans } from "react-i18next";
import { ActividadContext } from "./ActividadesUtils/BlockWrapper";
import { CKEditor } from "@ckeditor/ckeditor5-react";
import { Editor as ClassicEditor } from "ckeditor5-resize-nimbus-build/build/ckeditor";
import { htmlSupportConfig } from "../ActividadesUtils/FormularioUtils";
import ModalLg from "../../../fragments/Modals/ModalLg";
import { TABLE_STYLES } from "./ActividadesUtils/ActividadesUtils";
import { UserContext } from "../../../services/user";

const EditIcon = ({ className = "" }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden="true"
  >
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" />
    <path d="M20.71 7.04a1 1 0 0 0 0-1.41l-2.34-2.34a1 1 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
  </svg>
);

function PreguntasAbiertasTablas({
  preguntas,
  instrucciones,
  variant = "emerald",
  previewMode = false,
  transpose = true,
  showFirstItem = true,
  isGeneratingPDF = false,
}) {
  const [inputList, setInputList] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isAnswered, setIsAnswered] = useState(true);
  const [currentEditing, setCurrentEditing] = useState(null);
  const [originalAnswer, setOriginalAnswer] = useState("");
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const ctx = useContext(ActividadContext);
  const user = useContext(UserContext);
  const activityContext = ctx || {
    userData: "",
    loading: false,
    postCalificacionActividad: () => {},
  };

  useEffect(() => {
    const userResponses = activityContext?.userData;
    if (userResponses && userResponses !== "") {
      const parsedSelected = JSON.parse(userResponses);
      setInputList(parsedSelected);
    }
  }, []);

  const rows = Array.isArray(preguntas) ? preguntas : [];
  if (!rows.length) return null;
  const headerRow = rows[0] || {};
  const headerCells = Array.from(
    new Map(
      (headerRow.respuestas || []).map((r) => [r.columnId, r.texto || ""]),
    ).entries(),
  );
  const s = TABLE_STYLES[variant] || TABLE_STYLES.nimbus;
  const headerCellMap = new Map(headerCells);
  const colIds = headerCells.map(([colId]) => colId);
  const originalRows = rows.slice(1);

  const calificar = () => {
    setIsAnswered(true);
    const dataToSave = inputList.map((el) => ({
      answer: el.answer ? el.answer : "",
      _id: el._id,
    }));
    activityContext.postCalificacionActividad(100, JSON.stringify(dataToSave));
  };

  const handleInput = (answer, inputId) => {
    setInputList((prev) => {
      const idx = prev.findIndex((el) => el._id === inputId);
      if (idx !== -1) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], answer };
        return copy;
      }
      return [...prev, { _id: inputId, answer }];
    });
  };

  return (
    <>
      {!previewMode && (
        <ModalLg
          isOpen={modalIsOpen}
          toggleModal={() => setModalIsOpen(!modalIsOpen)}
        >
          {currentEditing && (
            <CKEditor
              editor={ClassicEditor}
              data={currentEditing?.answer || ""}
              config={{
                placeholder: " ",
                htmlSupport: htmlSupportConfig,
                toolbar: [
                  "fontColor",
                  "fontBackgroundColor",
                  "|",
                  "bold",
                  "italic",
                  "bulletedList",
                  "numberedList",
                  "blockQuote",
                  "insertTable",
                  "undo",
                  "redo",
                  "alignment",
                  "outdent",
                  "indent",
                ],
                table: {
                  contentToolbar: [
                    "tableColumn",
                    "tableRow",
                    "mergeTableCells",
                    "tableProperties",
                    "tableCellProperties",
                  ],
                },
              }}
              onChange={(_, editor) => {
                const data = editor.getData();
                const plainText = data
                  .replace(/<[^>]*>/g, "")
                  .replace(/&nbsp;/g, " ")
                  .trim();
                if (plainText.length <= 1000) {
                  handleInput(data, currentEditing._id);
                }
              }}
            />
          )}
          <div className="flex justify-end mt-4">
            <button
              onClick={() => {
                handleInput(originalAnswer, currentEditing._id);
                setModalIsOpen(false);
              }}
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 mr-3"
            >
              Cancelar
            </button>
            <button
              onClick={() => setModalIsOpen(false)}
              className="px-4 py-2  text-white rounded-md hover:opacity-90 bg-nimbus"
            >
              Guardar y cerrar
            </button>
          </div>
        </ModalLg>
      )}
      <div className="w-full">
        {instrucciones && (
          <div className="max-w-6xl mx-auto mb-6 text-gray-700 act-instructions">
            {instrucciones}
          </div>
        )}
        <div className="max-w-6xl mx-auto overflow-x-auto">
          <div className={s.container}>
            <table className={s.table}>
              <thead className={s.thead}>
                <tr>
                  <th className={`${s.th} w-56`}>
                    {showFirstItem
                      ? (() => {
                          const cornerId = `corner_${headerRow._id}`;
                          const cornerExisting = inputList.find(
                            (el) => el._id === cornerId,
                          );
                          const openCornerEditor = () => {
                            const existingAns = cornerExisting?.answer || "";
                            setCurrentEditing({
                              _id: cornerId,
                              answer: existingAns,
                            });
                            setOriginalAnswer(existingAns);
                            setModalIsOpen(true);
                          };
                          return headerRow.texto ? (
                            <span
                              dangerouslySetInnerHTML={{
                                __html: headerRow.texto,
                              }}
                            />
                          ) : cornerExisting?.answer ? (
                            <div
                              className="relative group"
                              onClick={
                                previewMode ? undefined : openCornerEditor
                              }
                            >
                              <div
                                className={`prose prose-sm max-w-none ${s.headerEditableText}`}
                                dangerouslySetInnerHTML={{
                                  __html: cornerExisting.answer,
                                }}
                              />
                              <EditIcon
                                className={`w-4 h-4 absolute top-1 right-1 ${s.iconHeader}`}
                              />
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={
                                previewMode ? undefined : openCornerEditor
                              }
                              className={s.buttonHeader}
                            >
                              <EditIcon className="w-4 h-4" />
                              <Trans>escribe tu respuesta</Trans>
                            </button>
                          );
                        })()
                      : null}
                  </th>
                  {transpose
                    ? originalRows.map((row) => {
                        const rowId = `row_${row._id}`;
                        const rowExisting = inputList.find(
                          (el) => el._id === rowId,
                        );
                        const openRowEditor = () => {
                          const existingAns = rowExisting?.answer || "";
                          setCurrentEditing({
                            _id: rowId,
                            answer: existingAns,
                          });
                          setOriginalAnswer(existingAns);
                          setModalIsOpen(true);
                        };
                        const label = row.texto;
                        return (
                          <th key={row._id} className={s.th}>
                            {label ? (
                              <span
                                dangerouslySetInnerHTML={{ __html: label }}
                              />
                            ) : rowExisting?.answer ? (
                              <div
                                className="relative group"
                                onClick={
                                  previewMode ? undefined : openRowEditor
                                }
                              >
                                <div
                                  className={`prose prose-sm max-w-none ${s.headerEditableText}`}
                                  dangerouslySetInnerHTML={{
                                    __html: rowExisting.answer,
                                  }}
                                />
                                <EditIcon
                                  className={`w-4 h-4 absolute top-1 right-1 ${s.iconHeader}`}
                                />
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={
                                  previewMode ? undefined : openRowEditor
                                }
                                className={s.buttonHeader}
                              >
                                <EditIcon className="w-4 h-4" />
                                <Trans>escribe tu respuesta</Trans>
                              </button>
                            )}
                          </th>
                        );
                      })
                    : headerCells.map(([colId, label]) => {
                        const headerId = `header_${colId}`;
                        const headerExisting = inputList.find(
                          (el) => el._id === headerId,
                        );
                        const openHeaderEditor = () => {
                          const existingAns = headerExisting?.answer || "";
                          setCurrentEditing({
                            _id: headerId,
                            answer: existingAns,
                          });
                          setOriginalAnswer(existingAns);
                          setModalIsOpen(true);
                        };
                        return (
                          <th key={colId} className={s.th}>
                            {label ? (
                              <span
                                dangerouslySetInnerHTML={{ __html: label }}
                              />
                            ) : headerExisting?.answer ? (
                              <div
                                className="relative group"
                                onClick={
                                  previewMode ? undefined : openHeaderEditor
                                }
                              >
                                <div
                                  className={`prose prose-sm max-w-none ${s.headerEditableText}`}
                                  dangerouslySetInnerHTML={{
                                    __html: headerExisting.answer,
                                  }}
                                />
                                <EditIcon
                                  className={`w-4 h-4 absolute top-1 right-1 ${s.iconHeader}`}
                                />
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={
                                  previewMode ? undefined : openHeaderEditor
                                }
                                className={s.buttonHeader}
                              >
                                <EditIcon className="w-4 h-4" />
                                <Trans>escribe tu respuesta</Trans>
                              </button>
                            )}
                          </th>
                        );
                      })}
                </tr>
              </thead>
              <tbody className={s.tbody}>
                {transpose
                  ? colIds.map((colId) => {
                      const headerId = `header_${colId}`;
                      const headerExisting = inputList.find(
                        (el) => el._id === headerId,
                      );
                      const openHeaderEditor = () => {
                        const existingAns = headerExisting?.answer || "";
                        setCurrentEditing({
                          _id: headerId,
                          answer: existingAns,
                        });
                        setOriginalAnswer(existingAns);
                        setModalIsOpen(true);
                      };
                      const label = headerCellMap.get(colId);
                      return (
                        <tr key={colId} className={s.tr}>
                          <td className={s.rowLabelTd}>
                            {label ? (
                              <span
                                dangerouslySetInnerHTML={{ __html: label }}
                              />
                            ) : headerExisting?.answer ? (
                              <div
                                className="relative group"
                                onClick={
                                  previewMode ? undefined : openHeaderEditor
                                }
                              >
                                <div
                                  className={`prose prose-sm max-w-none ${s.cellEditableText}`}
                                  dangerouslySetInnerHTML={{
                                    __html: headerExisting.answer,
                                  }}
                                />
                                <EditIcon
                                  className={`w-4 h-4 absolute top-1 right-1 ${s.iconCell}`}
                                />
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={
                                  previewMode ? undefined : openHeaderEditor
                                }
                                className={s.buttonCell}
                              >
                                <EditIcon className="w-4 h-4" />
                                <Trans>escribe tu respuesta</Trans>
                              </button>
                            )}
                          </td>
                          {originalRows.map((row) => {
                            const cell = row?.respuestas?.find(
                              (r) => r.columnId === colId,
                            );
                            const cellId = cell?._id || `${row._id}_${colId}`;
                            const existing = inputList.find(
                              (el) => el._id === cellId,
                            );
                            const openEditor = () => {
                              const existingAns = existing?.answer || "";
                              setCurrentEditing({
                                _id: cellId,
                                answer: existingAns,
                              });
                              setOriginalAnswer(existingAns);
                              setModalIsOpen(true);
                            };
                            return (
                              <td
                                key={`${row._id}_${colId}`}
                                className={s.bodyTd}
                              >
                                {cell.texto ? (
                                  <span
                                    dangerouslySetInnerHTML={{
                                      __html: cell.texto,
                                    }}
                                  />
                                ) : (
                                  <div className="cursor-pointer">
                                    {existing?.answer ? (
                                      <div
                                        className="relative group"
                                        onClick={
                                          previewMode ? undefined : openEditor
                                        }
                                      >
                                        <div
                                          className={`prose prose-sm max-w-none ${s.cellEditableText}`}
                                          dangerouslySetInnerHTML={{
                                            __html: existing.answer,
                                          }}
                                        />
                                        <EditIcon
                                          className={`w-4 h-4 absolute top-1 right-1 ${s.iconCell}`}
                                        />
                                      </div>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={
                                          previewMode ? undefined : openEditor
                                        }
                                        className={s.buttonCell}
                                      >
                                        <EditIcon className="w-4 h-4" />
                                        <Trans>escribe tu respuesta</Trans>
                                      </button>
                                    )}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })
                  : rows.slice(1).map((row) => {
                      const rowId = `row_${row._id}`;
                      const rowExisting = inputList.find(
                        (el) => el._id === rowId,
                      );
                      const openRowEditor = () => {
                        const existingAns = rowExisting?.answer || "";
                        setCurrentEditing({ _id: rowId, answer: existingAns });
                        setOriginalAnswer(existingAns);
                        setModalIsOpen(true);
                      };
                      return (
                        <tr key={row._id} className={s.tr}>
                          <td className={s.rowLabelTd}>
                            {row.texto ? (
                              <span
                                dangerouslySetInnerHTML={{ __html: row.texto }}
                              />
                            ) : rowExisting?.answer ? (
                              <div
                                className="relative group"
                                onClick={
                                  previewMode ? undefined : openRowEditor
                                }
                              >
                                <div
                                  className={`prose prose-sm max-w-none ${s.cellEditableText}`}
                                  dangerouslySetInnerHTML={{
                                    __html: rowExisting.answer,
                                  }}
                                />
                                <EditIcon
                                  className={`w-4 h-4 absolute top-1 right-1 ${s.iconCell}`}
                                />
                              </div>
                            ) : (
                              <button
                                type="button"
                                onClick={
                                  previewMode ? undefined : openRowEditor
                                }
                                className={s.buttonCell}
                              >
                                <EditIcon className="w-4 h-4" />
                                <Trans>escribe tu respuesta</Trans>
                              </button>
                            )}
                          </td>
                          {headerCells.map(([colId]) => {
                            const cell = row?.respuestas?.find(
                              (r) => r.columnId === colId,
                            );
                            const cellId = cell?._id || `${row._id}_${colId}`;
                            const existing = inputList.find(
                              (el) => el._id === cellId,
                            );
                            const openEditor = () => {
                              const existingAns = existing?.answer || "";
                              setCurrentEditing({
                                _id: cellId,
                                answer: existingAns,
                              });
                              setOriginalAnswer(existingAns);
                              setModalIsOpen(true);
                            };
                            return (
                              <td key={colId} className={s.bodyTd}>
                                {cell.texto ? (
                                  <span
                                    dangerouslySetInnerHTML={{
                                      __html: cell.texto,
                                    }}
                                  />
                                ) : (
                                  <div className="cursor-pointer">
                                    {existing?.answer ? (
                                      <div
                                        className="relative group"
                                        onClick={
                                          previewMode ? undefined : openEditor
                                        }
                                      >
                                        <div
                                          className={`prose prose-sm max-w-none ${s.cellEditableText}`}
                                          dangerouslySetInnerHTML={{
                                            __html: existing.answer,
                                          }}
                                        />
                                        <EditIcon
                                          className={`w-4 h-4 absolute top-1 right-1 ${s.iconCell}`}
                                        />
                                      </div>
                                    ) : (
                                      <button
                                        type="button"
                                        onClick={
                                          previewMode ? undefined : openEditor
                                        }
                                        className={s.buttonCell}
                                      >
                                        <EditIcon className="w-4 h-4" />
                                        <Trans>escribe tu respuesta</Trans>
                                      </button>
                                    )}
                                  </div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
              </tbody>
            </table>{" "}
          </div>
        </div>
        {!previewMode && !isGeneratingPDF && user?.activeRole !== "PROFESOR" && (
          <div className="max-w-6xl mx-auto mt-5">
            <LoadingButton
              onClick={isAnswered ? calificar : undefined}
              isLoading={activityContext.loading}
            />
          </div>
        )}
      </div>
    </>
  );
}

export default PreguntasAbiertasTablas;
